import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx } from "vue"

import { Field } from 'vee-validate'
import { useGettext } from 'vue3-gettext'
import { computed, ref, toRefs, onMounted, watch } from 'vue'
import { useUserStore } from '@/store/user'
import NotificationUI from '@/components/CommonUI/NotificationUI.vue'
import MultiselectUI from '@/components/CommonUI/MultiselectUI.vue'
import { sortAlphabeticallyByName } from '@/helpers/sort'

interface Props {
  index: number,
  writers: Writer[],
  track: Track,
  disabled: boolean,
}


export default /*#__PURE__*/_defineComponent({
  __name: 'WriterNameSelector',
  props: {
    index: {},
    writers: {},
    track: {},
    disabled: { type: Boolean, default: false }
  },
  emits: ["updateOption"],
  setup(__props: any, { emit }) {

const props = __props;

const { $gettext } = useGettext()
const userStore = useUserStore()


const { writers, index } = toRefs(props)



const selectedOption = ref<LinkedAccountOption | null>(null)
const createOption = ref<WriterNameOptionsGroup>({
  group: $gettext('Create'),
  names: [],
})

function getCreateOptionGroup(): WriterNameOptionsGroup[] {
  return createOption.value.names.length
    ? [createOption.value]
    : []
}

function getLinkedAccountsOptionsGroup(): WriterNameOptionsGroup[] {
  const linkedAccounts = userStore.linkedAccounts.filter(
    ({ email, name }) => !writers.value.some(writer => writer.email === email && writer.name === name),
  )

  if (!linkedAccounts.length) {
    return []
  }

  const linkedAccountsOptions = linkedAccounts.map(user => ({
    ...user,
    nameEmail: `${user.name} ${user.email}`,
  } as LinkedAccountOption))

  return [{
    group: $gettext('Accepted registrations'),
    names: sortAlphabeticallyByName<LinkedAccountOption>(linkedAccountsOptions),
  }]
}

const writerNamesOptions = computed(() => [
  ...getCreateOptionGroup(),
  ...getLinkedAccountsOptionsGroup(),
])

function selectWriterName(user: LinkedAccountOption, index: number) {
  selectedOption.value = user
  emit('updateOption', user, index)
}

function updateCreateOption(typedText: string, handleChange: (value: string) => void) {
  const newUser: LinkedAccountOption = {
    email: '',
    name: typedText,
    nameEmail: typedText,
  }
  selectedOption.value = newUser
  handleChange(typedText)

  if (!typedText.length) {
    selectedOption.value = null
    createOption.value.names = []

    return
  }

  createOption.value.names = [newUser]
}

function updateSelectedOption(selectedWriter: Writer) {
  if (selectedWriter.linked_user_id) {
    const linkedUser = userStore.getLinkedUserById(selectedWriter.linked_user_id)

    if (linkedUser) {
      selectedOption.value = {
        ...linkedUser,
        nameEmail: `${linkedUser.name} ${linkedUser.email}`,
      } as LinkedAccountOption
    }
  }
}

// watch for writers updates and new mounts when copying shares
watch(() => props.writers[props.index], selectedWriter => updateSelectedOption(selectedWriter))
onMounted(async () => {
  const selectedWriter = props.writers[props.index]
  updateSelectedOption(selectedWriter)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Field), {
    name: `writers[${_unref(index)}].name`
  }, {
    default: _withCtx(({ handleChange, errorMessage }) => [
      _createVNode(MultiselectUI, {
        label: "name",
        "group-label": "group",
        "group-values": "names",
        "track-by": "nameEmail",
        name: `writers[${_unref(index)}].name`,
        "input-label": _unref($gettext)('Legal Name'),
        "tooltip-info-text": _unref($gettext)('When you provide Writer information for a track, please ensure that you use the full legal name of the Writer - not the Artist name.'),
        "hide-label": _unref(index) > 0,
        "model-value": selectedOption.value,
        multiple: false,
        "clear-on-select": false,
        "hide-selected": false,
        "show-before-list": false,
        placeholder: _unref($gettext)('Start typing'),
        options: writerNamesOptions.value,
        "has-error": !!errorMessage,
        disabled: _ctx.disabled,
        onSearchChange: (typedText) => updateCreateOption(typedText, handleChange),
        onSelect: _cache[0] || (_cache[0] = (user) => selectWriterName(user, _unref(index)))
      }, null, 8, ["name", "input-label", "tooltip-info-text", "hide-label", "model-value", "placeholder", "options", "has-error", "disabled", "onSearchChange"]),
      errorMessage
        ? (_openBlock(), _createBlock(NotificationUI, {
            key: 0,
            message: errorMessage,
            type: "error outline",
            "icon-fill": "error",
            "icon-name": "danger"
          }, null, 8, ["message"]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }, 8, ["name"]))
}
}

})